/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ArrowUp, Paperclip } from "lucide-react";
import { useToast } from "@chakra-ui/react";
import { useSnapshot } from "valtio";
import { SupportTicket, TicketStatus } from "../../types/ticket";
import { getMyTicket } from "../../apis/ticket/get.my.ticket";
import { replyToMyTicket } from "../../apis/ticket/reply.to.my.ticket";
import { markMyTicketRead } from "../../apis/ticket/mark.my.ticket.read";
import { supportStore } from "../../utils/support";
import { MessageReplyTo } from "../../types/feedback";
import AttachmentGallery from "./attachment-gallery";
import {
  useAttachmentUpload,
  HiddenFileInput,
  PendingPreviews,
} from "./attachment-upload";
import { QuotedBlock, ReplyingToBar, ReplyButton, previewOf } from "./reply";

const STATUS_META: Record<TicketStatus, { label: string; color: string }> = {
  open: { label: "Open", color: "#F59E0B" },
  in_progress: { label: "In progress", color: "#3B82F6" },
  resolved: { label: "Resolved", color: "#10B981" },
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

interface Props {
  ticketId: string;
  onBack: () => void;
}

// TicketThread is a ticket's full conversation, opened as its own view (not
// inline in the chat) so there's a single composer. Back returns to the chat.
const TicketThread: React.FC<Props> = ({ ticketId, onBack }) => {
  const toast = useToast({ position: "top", containerStyle: { zIndex: 9 } });
  const { liveTick } = useSnapshot(supportStore);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTarget, setReplyTarget] = useState<MessageReplyTo | null>(null);
  const { pending, uploading, fileRef, pick, onFilesSelected, removeAt, reset } =
    useAttachmentUpload();
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = () => {
    getMyTicket(
      {
        params: ticketId,
        onCompleted({ data }) {
          setTicket(data);
          setLoading(false);
          markMyTicketRead({
            params: `${ticketId}/read`,
            onCompleted() {},
            onError() {},
          });
        },
        onError() {
          setLoading(false);
        },
      },
      { bypassCache: true }
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Live refresh on admin activity.
  const firstTick = useRef(true);
  useEffect(() => {
    if (firstTick.current) {
      firstTick.current = false;
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTick]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages?.length]);

  const isResolved = ticket?.status === "resolved";
  const meta = STATUS_META[ticket?.status ?? "open"] ?? STATUS_META.open;

  const handleSend = () => {
    const body = reply.trim();
    if ((!body && pending.length === 0) || sending) return;
    setSending(true);
    replyToMyTicket({
      params: `${ticketId}/reply`,
      body: {
        body,
        attachments: pending.length ? pending : undefined,
        replyTo: replyTarget ?? undefined,
      },
      onCompleted({ data }) {
        setTicket(data);
        setReply("");
        reset();
        setReplyTarget(null);
        setSending(false);
      },
      onError(err) {
        setSending(false);
        toast({
          status: "error",
          description: typeof err === "string" ? err : "Failed to send",
        });
      },
    });
  };

  const messages = ticket?.messages ?? [];

  return (
    <div className="flex flex-col h-full w-full min-h-0 bg-[#F9F9F9]">
      {/* Back bar */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-200 bg-white">
        <button
          onClick={onBack}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Back to chat"
        >
          <ChevronLeft size={22} className="text-gray-900" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {ticket?.subject || "Ticket"}
          </p>
          <span
            className="text-[11px] px-2 py-0.5 rounded-full"
            style={{ color: meta.color, backgroundColor: `${meta.color}1A` }}
          >
            {meta.label}
          </span>
        </div>
      </div>

      <div className="flex-1 px-6 py-6 overflow-y-auto min-h-0">
        {loading ? (
          <p className="text-center text-gray-400 text-sm">Loading…</p>
        ) : (
          <>
            {isResolved && ticket?.resolution && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                <p className="text-[11px] font-semibold text-green-700">
                  Resolution
                </p>
                <p className="text-sm text-green-800 whitespace-pre-wrap">
                  {ticket.resolution}
                </p>
              </div>
            )}
            {ticket?.sourceBody && (
              <div className="mb-4 rounded-lg bg-gray-100 px-3 py-2">
                <p className="text-[11px] font-semibold text-gray-500">
                  Raised from your message
                </p>
                <p className="text-sm text-gray-700">{ticket.sourceBody}</p>
              </div>
            )}
            {messages.length === 0 && (
              <p className="text-center text-gray-400 text-sm mt-2">
                No messages on this ticket yet.
              </p>
            )}
            {messages.map((m) => {
              const isUser = m.sender === "user";
              return (
                <div
                  key={m._id}
                  className={`mb-4 flex items-center gap-1 ${
                    isUser ? "justify-end" : "justify-start"
                  }`}
                >
                  {isUser && (
                    <ReplyButton
                      onClick={() =>
                        setReplyTarget({
                          messageID: m._id,
                          sender: m.sender,
                          preview: previewOf(m),
                        })
                      }
                    />
                  )}
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3 shadow-sm ${
                      isUser ? "bg-white" : "bg-[#FFE4E4]"
                    }`}
                  >
                    {m.replyTo && (
                      <QuotedBlock replyTo={m.replyTo} viewerSide="user" />
                    )}
                    {m.body && (
                      <p className="text-gray-900 text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {m.body}
                      </p>
                    )}
                    <AttachmentGallery items={m.attachments} />
                    <p className="text-[10px] text-gray-400 text-right mt-1 whitespace-nowrap">
                      {formatTime(m.createdAt)}
                    </p>
                  </div>
                  {!isUser && (
                    <ReplyButton
                      onClick={() =>
                        setReplyTarget({
                          messageID: m._id,
                          sender: m.sender,
                          preview: previewOf(m),
                        })
                      }
                    />
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {isResolved ? (
        <p className="text-center text-gray-400 text-xs py-4 bg-[#F9F9F9]">
          This ticket is resolved.
        </p>
      ) : (
        <div className="px-6 pb-6 pt-4 bg-[#F9F9F9]">
          {replyTarget && (
            <ReplyingToBar
              target={replyTarget}
              viewerSide="user"
              onCancel={() => setReplyTarget(null)}
            />
          )}
          <PendingPreviews items={pending} onRemove={removeAt} />
          <div className="relative">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Reply to this ticket…"
              className="w-full min-h-[60px] max-h-[120px] p-4 pb-12 pr-28 rounded-lg border border-gray-200 bg-white resize-none focus:outline-none focus:border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
            <div className="absolute bottom-3 right-3 flex items-center gap-2">
              <HiddenFileInput fileRef={fileRef} onChange={onFilesSelected} />
              <button
                onClick={pick}
                disabled={uploading}
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50"
                aria-label="Attach"
              >
                {uploading ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Paperclip size={16} className="text-gray-600" />
                )}
              </button>
              <button
                onClick={handleSend}
                disabled={(!reply.trim() && pending.length === 0) || sending}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  reply.trim() || pending.length
                    ? "bg-[#1E3A8A] hover:bg-[#1e40af]"
                    : "bg-gray-300"
                }`}
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ArrowUp
                    size={20}
                    className={
                      reply.trim() || pending.length
                        ? "text-white"
                        : "text-gray-500"
                    }
                  />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketThread;
